/* iter367 Phase-2 — Admin Oncology Verifications panel.
 *
 * Manages clinician license verification SPECIFIC to the oncology
 * educational modules (separate from the general /admin/clinician-applications
 * flow used for Doctor Portal access). Approving here promotes the user's
 * role to "clinician" and unlocks /api/oncology/modules/* endpoints.
 *
 * Routes consumed:
 *   GET  /api/oncology/verification/pending   — admin-only list of pending apps
 *   POST /api/oncology/verification/{user_id}/decision  — approve/reject
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Microscope, Check, X as XIcon, Loader2, ChevronDown, ChevronUp,
  ExternalLink, ShieldCheck, Mail, Building2, FileText, Globe,
} from "lucide-react";

export default function OncologyVerificationsPanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/oncology/verification/pending`);
      setPending(data?.pending || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [API, bg]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const decide = async (userId, approved) => {
    setReviewLoading(userId);
    try {
      await axios.post(`${API}/oncology/verification/${userId}/decision`, {
        approved,
        reviewer_notes: approved ? "" : rejectNotes,
      });
      toast.success(approved
        ? (bg ? "Клиницистът е одобрен — онко-модулите са отключени." : "Clinician approved — oncology modules unlocked.")
        : (bg ? "Заявката е отхвърлена." : "Application rejected."));
      setRejectNotes("");
      setExpandedUserId(null);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setReviewLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="oncology-verifications-panel">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Microscope className="w-5 h-5 text-fuchsia-500" />
          <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
            {bg ? "Онко-модули — лицензни верификации" : "Oncology modules — license verifications"}
          </h2>
          {pending.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="onco-pending-badge">
              {pending.length} {bg ? "за преглед" : "pending"}
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchPending} data-testid="onco-verif-refresh">
          {bg ? "Обнови" : "Refresh"}
        </Button>
      </header>

      <p className={`text-[11px] leading-relaxed rounded-lg p-2.5 ${isDark ? "bg-amber-500/10 border border-amber-500/30 text-amber-200" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
        {bg
          ? "Одобрението отключва образователния онкологичен режим (EU AI Act, ограничен риск). Уверете се, че медицинският лиценз и специалност съответстват на онкология / клинична диетология."
          : "Approval unlocks the educational oncology mode (EU AI Act limited-risk). Verify the medical license and specialty align with oncology / clinical dietetics."}
      </p>

      {pending.length === 0 ? (
        <div className={`text-sm text-center py-8 rounded-xl border ${isDark ? "bg-white/5 border-white/10 text-gray-400" : "bg-white border-gray-200 text-gray-500"}`} data-testid="onco-no-pending">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          {bg ? "Няма чакащи заявки." : "No pending applications."}
        </div>
      ) : (
        <ul className="space-y-2">
          {pending.map((app) => {
            const expanded = expandedUserId === app.user_id;
            return (
              <li
                key={app.user_id}
                className={`rounded-xl border ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}
                data-testid={`onco-verif-card-${app.user_id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedUserId(expanded ? null : app.user_id)}
                  className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                      {app.user_email || "—"}
                    </div>
                    <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {app.specialty} · {app.license_country} · #{app.license_number}
                    </div>
                  </div>
                  {expanded ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
                </button>

                {expanded && (
                  <div className={`px-4 py-3 border-t ${isDark ? "border-white/10" : "border-gray-200"} space-y-2`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                      <Field icon={<Mail className="w-3 h-3" />} label={bg ? "Имейл" : "Email"} value={app.user_email} />
                      <Field icon={<Mail className="w-3 h-3" />} label={bg ? "Служебен" : "Pro email"} value={app.professional_email} />
                      <Field icon={<FileText className="w-3 h-3" />} label={bg ? "Лиценз №" : "License #"} value={app.license_number} />
                      <Field icon={<Globe className="w-3 h-3" />} label={bg ? "Държава" : "Country"} value={app.license_country} />
                      <Field icon={<Building2 className="w-3 h-3" />} label={bg ? "Издаващ орган" : "Issuer"} value={app.issuing_authority} />
                      <Field icon={<Microscope className="w-3 h-3" />} label={bg ? "Специалност" : "Specialty"} value={app.specialty} />
                    </div>
                    {app.license_url && (
                      <a
                        href={app.license_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[12px] text-fuchsia-500 hover:underline"
                        data-testid={`onco-verif-license-link-${app.user_id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {bg ? "Преглед на сканиран лиценз" : "View scanned license"}
                      </a>
                    )}
                    {app.notes && (
                      <p className={`text-[11px] italic ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {bg ? "Бележки от клинициста:" : "Clinician notes:"} {app.notes}
                      </p>
                    )}
                    <p className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                      {bg ? "Изпратено:" : "Submitted:"} {new Date(app.submitted_at).toLocaleString()}
                    </p>

                    <textarea
                      placeholder={bg ? "Причина за отхвърляне (по избор, ще се запази в одита)" : "Reject reason (optional, will be stored in audit)"}
                      value={rejectNotes}
                      onChange={(e) => setRejectNotes(e.target.value)}
                      maxLength={600}
                      rows={2}
                      data-testid={`onco-verif-reject-notes-${app.user_id}`}
                      className={`w-full text-[12px] px-2 py-1.5 rounded border ${isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-white border-gray-200 placeholder-gray-400"}`}
                    />

                    <div className="flex gap-2 pt-1">
                      <Button
                        onClick={() => decide(app.user_id, true)}
                        disabled={!!reviewLoading}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`onco-verif-approve-${app.user_id}`}
                      >
                        {reviewLoading === app.user_id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                        {bg ? "Одобри" : "Approve"}
                      </Button>
                      <Button
                        onClick={() => decide(app.user_id, false)}
                        disabled={!!reviewLoading}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`onco-verif-reject-${app.user_id}`}
                      >
                        {reviewLoading === app.user_id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XIcon className="w-4 h-4 mr-1" />}
                        {bg ? "Отхвърли" : "Reject"}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Field({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-1.5">
      <span className="opacity-60 mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider opacity-60">{label}</div>
        <div className="text-[12px] break-words">{value}</div>
      </div>
    </div>
  );
}
